import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import type { PlanReview, QtyInBoxIssue } from "@/api/order_check";
import { type PlanSavePallet, getPlanReview, savePlan } from "@/api/order_plan";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";
import { fmtDate } from "@/lib/format";
import { boxWarning, prefillBoxes, quantumLabel } from "@/lib/plan-boxes";
import { cn } from "@/lib/utils";
import { palletNumber } from "@/pages/order-check/v2/pallet-number";

/**
 * Раскладка заказа по паллетам: сверка с заказом и ручная перекладка.
 *
 * Раскладку делает бот ДО сборки, потом заказ правят руками, а план паллет за
 * правкой не ходит. Склад узнаёт об этом на скане — гейт плана отбивает каждый
 * код, и заказ не собрать никак. План живёт в портале (реестр грузомест):
 * сохранение — новая его ревизия, номер новой паллеты — наш SSCC.
 *
 * ⚠️ Автоматики здесь НЕТ намеренно. Портал не выбирает партию и не ходит в
 * остатки: всё, что можно положить на паллету, уже названо строкой заказа, и
 * раскладку раскидывает человек. Сохранить можно, только когда расхождений
 * нет — «сохраню как есть, доразложу потом» оставило бы раскладку, по
 * которой заказ не собрать.
 */
const EPS = 0.001;

interface EditLine {
  key: string;
  nom: string;
  char: string;
  partia: string;
  qty: number;
  boxes: number;
  /** Строка, которой нет в заказе: снять перед сохранением. */
  stray: boolean;
  label: string;
  name: string;
  /** Штук в коробе; `null` — неизвестен (причина — `qtyInBoxIssue`). */
  qtyInBox: number | null;
  qtyInBoxIssue: QtyInBoxIssue;
  /** Короба правили руками в этой строке — автоподстановка их не трогает. */
  boxesTouched: boolean;
  /** Строка как пришла с сервера, с предупреждением бэкенда. Пока человек её не
   *  правил, показываем серверный текст; правленая — считается тут же. */
  server: { qty: number; boxes: number; warning: string } | null;
}

/** Предупреждение по коробам строки: серверное, пока строка не правлена. */
function lineBoxWarning(li: EditLine): string {
  if (li.server && li.server.qty === li.qty && li.server.boxes === li.boxes)
    return li.server.warning;
  return boxWarning(li.qty, li.boxes, li.qtyInBox);
}

/** Новое количество строки; короба подставляются по кванту, только если
 *  делится нацело и короба в этой строке руками не трогали. */
function withQty(li: EditLine, qty: number): EditLine {
  const boxes = li.boxesTouched ? null : prefillBoxes(qty, li.qtyInBox);
  return { ...li, qty, boxes: boxes ?? li.boxes };
}

interface EditPallet {
  ref: string;
  number: string;
  lines: EditLine[];
}

const keyOf = (nom: string, char: string, partia: string) => `${nom}|${char}|${partia}`;

function fmt(q: number): string {
  return Number.isInteger(q) ? String(q) : String(Number(q.toFixed(3)));
}

function needLabel(n: {
  nomenclature_article: string;
  nomenclature_name: string;
  characteristic_name: string;
  partia_name: string;
}): string {
  const parts = [n.nomenclature_article || n.nomenclature_name];
  if (n.characteristic_name) parts.push(n.characteristic_name);
  if (n.partia_name) parts.push(`партия ${n.partia_name}`);
  return parts.join(" · ");
}

function fromReview(d: PlanReview): EditPallet[] {
  return d.pallets.map((p) => ({
    ref: p.ref,
    number: p.number,
    lines: p.lines.map((li) => ({
      key: keyOf(li.nomenclature_ref, li.characteristic_ref, li.partia_ref),
      nom: li.nomenclature_ref,
      char: li.characteristic_ref,
      partia: li.partia_ref,
      qty: li.qty,
      boxes: li.boxes,
      stray: !li.in_order,
      label: needLabel(li),
      name: li.nomenclature_name,
      qtyInBox: li.qty_in_box,
      qtyInBoxIssue: li.qty_in_box_issue,
      boxesTouched: false,
      server: { qty: li.qty, boxes: li.boxes, warning: li.box_warning },
    })),
  }));
}

export function PlanReviewPage(): React.ReactElement {
  const { ref = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["plan-review", ref],
    queryFn: () => getPlanReview(ref),
    refetchOnWindowFocus: false,
  });

  const [pallets, setPallets] = useState<EditPallet[]>([]);
  const [dirty, setDirty] = useState(false);

  // Черновик живёт в браузере: правит его один человек в один заход, и
  // серверный черновик здесь был бы механикой ради механики. Цена — потеря
  // при перезагрузке, поэтому уход со страницы с несохранённым спрашивается.
  useEffect(() => {
    if (q.data) {
      setPallets(fromReview(q.data));
      setDirty(false);
    }
  }, [q.data]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const placed = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of pallets)
      for (const li of p.lines) m.set(li.key, (m.get(li.key) ?? 0) + li.qty);
    return m;
  }, [pallets]);

  const needs = q.data?.needs ?? [];
  const rows = needs.map((n) => {
    const key = keyOf(n.nomenclature_ref, n.characteristic_ref, n.partia_ref);
    const done = placed.get(key) ?? 0;
    return { n, key, done, left: n.qty_needed - done };
  });
  const strays = pallets.flatMap((p) => p.lines.filter((li) => li.stray));
  const balanced = rows.every((r) => Math.abs(r.left) <= EPS) && strays.length === 0;

  const save = useMutation({
    mutationFn: () => {
      const payload: PlanSavePallet[] = pallets.map((p) => ({
        ref: p.ref,
        lines: p.lines.map((li) => ({
          nomenclature_ref: li.nom,
          characteristic_ref: li.char,
          partia_ref: li.partia,
          qty: li.qty,
          boxes: li.boxes,
        })),
      }));
      return savePlan(ref, payload);
    },
    onSuccess: (res) => {
      const bits = [
        res.updated ? `изменено ${res.updated}` : "",
        res.created ? `заведено ${res.created}` : "",
        res.deleted ? `снято ${res.deleted}` : "",
      ].filter(Boolean);
      toast.success(`Раскладка сохранена: ${bits.join(", ") || "без изменений"}`);
      if (res.new_numbers.length)
        toast.info(
          `Новые паллеты: ${res.new_numbers
            .map((sscc) => palletNumber({ sscc, one_c_number: null }))
            .join(", ")}`,
        );
      qc.setQueryData(["plan-review", ref], res.review);
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось записать раскладку")),
  });

  const edit = (fn: (draft: EditPallet[]) => EditPallet[]) => {
    setPallets((prev) => fn(prev));
    setDirty(true);
  };

  /** Правка строк ОДНОЙ паллеты. Через map по индексу, а не мутацией
   *  `draft[pi]`: индекс может не существовать, и молчаливое падение на
   *  `undefined.lines` не то, что должно случаться при клике. */
  const onPallet = (pi: number, fn: (lines: EditLine[]) => EditLine[]) =>
    edit((d) => d.map((p, i) => (i === pi ? { ...p, lines: fn(p.lines) } : p)));

  const setQty = (pi: number, key: string, qty: number) =>
    onPallet(pi, (lines) => lines.map((li) => (li.key === key ? withQty(li, qty) : li)));

  const setBoxes = (pi: number, key: string, boxes: number) =>
    onPallet(pi, (lines) =>
      lines.map((li) => (li.key === key ? { ...li, boxes, boxesTouched: true } : li)),
    );

  const removeLine = (pi: number, key: string) =>
    onPallet(pi, (lines) => lines.filter((li) => li.key !== key));

  /** Положить ВЕСЬ остаток строки заказа на паллету. Дробить — руками. */
  const putRest = (pi: number, r: (typeof rows)[number]) =>
    onPallet(pi, (lines) =>
      lines.some((li) => li.key === r.key)
        ? lines.map((li) => (li.key === r.key ? withQty(li, li.qty + r.left) : li))
        : [
            ...lines,
            withQty(
              {
                key: r.key,
                nom: r.n.nomenclature_ref,
                char: r.n.characteristic_ref,
                partia: r.n.partia_ref,
                qty: 0,
                boxes: 0,
                stray: false,
                label: needLabel(r.n),
                name: r.n.nomenclature_name,
                qtyInBox: r.n.qty_in_box,
                qtyInBoxIssue: r.n.qty_in_box_issue,
                boxesTouched: false,
                server: null,
              },
              r.left,
            ),
          ],
    );

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 className="size-4 animate-spin" /> Читаем заказ из 1С и раскладку…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="space-y-3 p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Не удалось получить сверку: {extractErrorMessage(q.error, "ошибка")}
        </div>
        <Button variant="outline" onClick={() => q.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }

  const d = q.data;
  const ro = !d.editable;
  const noCatalog = d.needs.some((n) => n.qty_in_box_issue === "no_catalog");

  return (
    <div className="mx-auto max-w-[1400px] space-y-4 p-4">
      <header className="flex flex-wrap items-center gap-3">
        {/* Назад — В СПИСОК РАСКЛАДКИ, а не в сборку: раздел свой, и уводить
            старшего склада на скан-экран сборщика незачем. */}
        <Link
          to="/order-plan"
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="size-4" /> к списку
        </Link>
        <h1 className="text-lg font-bold">Раскладка заказа {d.order_number}</h1>
        <span className="text-sm text-slate-500">
          {d.kontragent_name}
          {d.ship_date ? ` · отгрузка ${fmtDate(d.ship_date)}` : ""}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-700">есть несохранённое</span>}
          <Button
            disabled={ro || !balanced || !dirty || save.isPending}
            title={
              ro
                ? d.locked_reason
                : !balanced
                  ? "Сохранить можно, только когда расхождений нет"
                  : "Сохранить раскладку"
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? <Loader2 className="mr-1 size-4 animate-spin" /> : null}
            Сохранить
          </Button>
        </div>
      </header>

      {ro && d.locked_reason && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {d.locked_reason}
        </div>
      )}

      {balanced ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
          <CheckCircle2 className="size-5" />
          <span className="font-semibold">
            {d.has_plan || dirty
              ? "Раскладка сходится с заказом."
              : "Раскладки нет — заказ можно разложить здесь."}
          </span>
        </div>
      ) : (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <div className="mb-1 flex items-center gap-2 font-semibold text-red-800">
            <AlertTriangle className="size-5" />
            Не разложено или разложено лишнее
          </div>
          <ul className="space-y-0.5 text-sm text-red-900">
            {rows
              .filter((r) => Math.abs(r.left) > EPS)
              .map((r) => (
                <li key={r.key}>
                  • {needLabel(r.n)}:{" "}
                  {r.left > 0
                    ? `не разложено ${fmt(r.left)} из ${fmt(r.n.qty_needed)}`
                    : `лишнее ${fmt(-r.left)}`}
                </li>
              ))}
            {strays.map((li) => (
              <li key={li.key}>• {li.label}: такой строки в заказе нет — снимите её</li>
            ))}
          </ul>
        </div>
      )}

      {noCatalog && (
        <div className="text-xs text-slate-500">
          Штук в коробе портал не показывает: у этой инсталляции нет справочника привязок
          маркетплейсов в 1С (marketplaceproducts) — короба сверяйте сами.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase text-slate-500">
            Заказ ({rows.length})
          </h2>
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase text-slate-400">
              <tr>
                <th className="pb-1">позиция</th>
                <th className="pb-1 text-right">нужно</th>
                <th className="pb-1 text-right">разложено</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const bad = Math.abs(r.left) > EPS;
                return (
                  <tr key={r.key} className={cn("border-t align-top", bad && "bg-red-50")}>
                    <td className="py-1.5 pr-2">
                      <div className="font-mono text-[13px] font-bold">{needLabel(r.n)}</div>
                      <div className="text-[11px] text-slate-500">
                        {r.n.nomenclature_name}
                        <QuantumTag qtyInBox={r.n.qty_in_box} issue={r.n.qty_in_box_issue} />
                      </div>
                    </td>
                    <td className="py-1.5 text-right font-mono tabular-nums">
                      {fmt(r.n.qty_needed)}
                    </td>
                    <td
                      className={cn(
                        "py-1.5 text-right font-mono tabular-nums",
                        bad && "font-bold text-red-700",
                      )}
                    >
                      {fmt(r.done)}
                      {bad && <span className="ml-1 text-[11px]">({fmt(-r.left)})</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase text-slate-500">
              Паллеты ({pallets.length})
            </h2>
            {!ro && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  edit((dr) => [...dr, { ref: "", number: "", lines: [] }])
                }
              >
                <Plus className="mr-1 size-4" /> Паллета
              </Button>
            )}
          </div>

          {pallets.map((p, pi) => (
            <div key={p.ref || `new-${pi}`} className="rounded-lg border bg-white p-3">
              <div className="mb-2 flex items-baseline justify-between">
                <span className="text-sm font-bold">
                  Паллета {pi + 1}
                  {p.number ? (
                    <span className="ml-1 font-mono font-normal text-slate-600">
                      {palletNumber({ sscc: p.number, one_c_number: null })}
                    </span>
                  ) : (
                    <span className="ml-1 rounded bg-sky-100 px-1 text-[10px] font-normal text-sky-800">
                      {p.ref ? "SSCC — при сохранении или открытии сборки" : "новая · SSCC — при сохранении"}
                    </span>
                  )}
                </span>
                <span className="text-[12px] text-slate-500">
                  {fmt(p.lines.reduce((s, li) => s + li.qty, 0))} шт ·{" "}
                  {fmt(p.lines.reduce((s, li) => s + li.boxes, 0))} кор
                </span>
              </div>

              <ul className="space-y-1">
                {p.lines.map((li) => {
                  const warning = lineBoxWarning(li);
                  return (
                    <li
                      key={li.key}
                      className={cn(
                        "rounded px-1.5 py-1 text-[12px]",
                        li.stray && "bg-red-50 text-red-900",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="font-mono font-bold">{li.label}</span>
                          {li.stray && <span className="ml-1 font-semibold">— нет в заказе</span>}
                          <QuantumTag qtyInBox={li.qtyInBox} issue={li.qtyInBoxIssue} />
                        </span>
                        <Input
                          type="number"
                          value={li.qty}
                          disabled={ro}
                          onChange={(e) => setQty(pi, li.key, Number(e.target.value) || 0)}
                          className="h-8 w-20 text-right"
                          title="Количество, базовых штук"
                        />
                        <Input
                          type="number"
                          value={li.boxes}
                          disabled={ro}
                          onChange={(e) => setBoxes(pi, li.key, Number(e.target.value) || 0)}
                          className="h-8 w-16 text-right"
                          title="Коробов. Подставляется по кванту, если штуки делятся нацело; правка руками — главнее"
                        />
                        {!ro && (
                          <button
                            type="button"
                            onClick={() => removeLine(pi, li.key)}
                            aria-label="Снять с паллеты"
                          >
                            <Trash2 className="size-4 text-rose-600" />
                          </button>
                        )}
                      </div>
                      {/* Предупреждение, не расхождение: янтарным, сохранение не держит. */}
                      {warning && (
                        <div className="mt-0.5 text-[11px] text-amber-700" role="note">
                          {warning}
                        </div>
                      )}
                    </li>
                  );
                })}
                {p.lines.length === 0 && (
                  <li className="text-[12px] text-slate-400">
                    Паллета пустая — при сохранении будет снята
                  </li>
                )}
              </ul>

              {!ro && rows.some((r) => r.left > EPS) && (
                <div className="mt-2 flex flex-wrap gap-1 border-t pt-2">
                  {rows
                    .filter((r) => r.left > EPS)
                    .map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => putRest(pi, r)}
                        className="rounded border bg-slate-50 px-1.5 py-0.5 text-[11px] hover:bg-slate-100"
                        title="Положить весь остаток этой позиции на паллету"
                      >
                        + {needLabel(r.n)} ({fmt(r.left)})
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

function QuantumTag({
  qtyInBox,
  issue,
}: {
  qtyInBox: number | null;
  issue: QtyInBoxIssue;
}): React.ReactElement | null {
  const text = quantumLabel(qtyInBox, issue);
  if (!text) return null;
  return (
    <span
      className={cn(
        "ml-1.5 whitespace-nowrap text-[11px] font-normal",
        qtyInBox ? "text-slate-500" : "text-amber-700",
      )}
    >
      {text}
    </span>
  );
}
